import {LoadingDots} from '@commercial-helios/ui/src/components/loading-dots'
import {colors, radii, zIndex, tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import SEO from '@/components/seo'
import type {ExpenseReportGeneratorQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import createPersistedReducer from '@/hooks/use-persisted-reducer'
import {useStepWizard} from '@/hooks/use-step-wizard'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {usePDFValues} from '@/utils/html-to-pdf'
import {scrollToTop} from '@/utils/scroll'
import RichText from '@helios/shared/components/rich-text'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import menuCloseIcon from '@helios/shared/images/icons/menu-close.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {usePageInfo} from '@helios/shared/providers/page-info'
import {Section} from '@helios/shared/styles/grid'
import {text, Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import mq from '@helios/shared/utils/media-query'

import AlmostThere from './almost-there'
import {BREAKPOINT_SM} from './constants'
import ExpensesDashboard from './expenses-dashboard'
import Header from './header'
import Preview from './preview'
import SuccessScreen from './success-screen'
import {useTrackFirstInteraction} from './tracking'
import type {Expense} from './types'

export interface FormsDataState {
    expenses: Expense[]
    fullName: string
    companyName: string
    companyLogoUrl: string | null
    reportNumber: string
    email: string
}

export enum ActionType {
    FULL_NAME_UPDATED,
    COMPANY_NAME_UPDATED,
    COMPANY_LOGO_URL_UPDATED,
    REPORT_NUMBER_UPDATED,
    EXPENSE_ADDED,
    EXPENSE_DELETED,
    EXPENSE_DATE_UPDATED,
    EXPENSE_CATEGORY_UPDATED,
    EXPENSE_MERCHANT_UPDATED,
    EXPENSE_DESCRIPTION_UPDATED,
    EXPENSE_PROJECT_UPDATED,
    EXPENSE_AMOUNT_UPDATED,
    EMAIL_UPDATED,
}

export type Action =
    | {
          type:
              | ActionType.FULL_NAME_UPDATED
              | ActionType.COMPANY_NAME_UPDATED
              | ActionType.REPORT_NUMBER_UPDATED
              | ActionType.EMAIL_UPDATED
          value: string
      }
    | {
          type: ActionType.EXPENSE_ADDED
      }
    | {
          type: ActionType.EXPENSE_DELETED
          id: Expense['id']
      }
    | {
          type:
              | ActionType.EXPENSE_DATE_UPDATED
              | ActionType.EXPENSE_CATEGORY_UPDATED
              | ActionType.EXPENSE_MERCHANT_UPDATED
              | ActionType.EXPENSE_DESCRIPTION_UPDATED
              | ActionType.EXPENSE_PROJECT_UPDATED
          id: Expense['id']
          value:
              | Expense['date']
              | Expense['category']
              | Expense['merchant']
              | Expense['description']
              | Expense['project']
      }
    | {
          type: ActionType.EXPENSE_AMOUNT_UPDATED
          id: Expense['id']
          value: Expense['amount']
      }
    | {
          type: ActionType.COMPANY_LOGO_URL_UPDATED
          value: string | null
      }

export type Dispatch = React.Dispatch<Action>

const patchExpenseInPlace = (
    expenses: Expense[],
    id: string,
    value: Record<string, string | Expense['amount']>
) => expenses.map((expense) => (expense.id === id ? {...expense, ...value} : expense))

const emptyExpense = (): Expense => ({
    id: Math.random().toString(16).substring(2),
    date: '',
    description: '',
    merchant: '',
    category: '',
    project: '',
    amount: undefined,
})

const reducer = (state: FormsDataState, action: Action): FormsDataState => {
    switch (action.type) {
        case ActionType.FULL_NAME_UPDATED: {
            return {
                ...state,
                fullName: action.value,
            }
        }

        case ActionType.COMPANY_NAME_UPDATED: {
            return {
                ...state,
                companyName: action.value,
            }
        }

        case ActionType.COMPANY_LOGO_URL_UPDATED: {
            return {
                ...state,
                companyLogoUrl: action.value,
            }
        }

        case ActionType.REPORT_NUMBER_UPDATED: {
            return {
                ...state,
                reportNumber: action.value,
            }
        }

        case ActionType.EXPENSE_ADDED: {
            return {
                ...state,
                expenses: state.expenses.concat(emptyExpense()),
            }
        }

        case ActionType.EXPENSE_DELETED: {
            return {
                ...state,
                expenses: state.expenses.filter((e) => e.id !== action.id),
            }
        }

        case ActionType.EXPENSE_DATE_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    date: action.value,
                }),
            }
        }

        case ActionType.EXPENSE_CATEGORY_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    category: action.value,
                }),
            }
        }

        case ActionType.EXPENSE_MERCHANT_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    merchant: action.value,
                }),
            }
        }

        case ActionType.EXPENSE_DESCRIPTION_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    description: action.value,
                }),
            }
        }

        case ActionType.EXPENSE_PROJECT_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    project: action.value,
                }),
            }
        }

        case ActionType.EXPENSE_AMOUNT_UPDATED: {
            return {
                ...state,
                expenses: patchExpenseInPlace(state.expenses, action.id, {
                    amount: action.value,
                }),
            }
        }

        case ActionType.EMAIL_UPDATED: {
            return {
                ...state,
                email: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const usePersistedReducer = createPersistedReducer<FormsDataState>('ExpenseReportGenerator')

const ExpenseReportGenerator = (props: PageProps<ExpenseReportGeneratorQuery>) => {
    const [isPreviewVisible, setIsPreviewVisible] = React.useState(false)
    const globalStrings = useGlobalStrings()
    const [shareableLink, setShareableLink] = React.useState('')
    const {pageContext} = usePageInfo()

    const isExternalVariant = () => pageContext.type === 'expense_report_generator_external_variant'
    const variant = isExternalVariant() ? 'external' : 'internal'

    const togglePreview = () => {
        scrollToTop()

        setIsPreviewVisible((isPreviewVisible) => !isPreviewVisible)
    }

    const initialState: FormsDataState = {
        expenses: [emptyExpense(), emptyExpense(), emptyExpense()],
        fullName: '',
        companyName: '',
        companyLogoUrl: null,
        reportNumber: '',
        email: '',
    }

    const [formsDataState, dispatch] = usePersistedReducer(reducer, initialState)
    const {previewValues, isPreviewMode} = usePDFValues('expense-report-generator', initialState)

    const content = props.data.allPrismicExpenseReportGenerator.nodes[0].data

    // append utm query param coming from pageContext to give information about referrer
    const utm = isExternalVariant()
        ? 'expense_report_generator_external_variant'
        : 'expense_report_generator_internal_variant'

    if ('url' in content?.headerCtaLink) {
        content.headerCtaLink.url = `${content.headerCtaLink.url}?utm=${utm}`
    }

    const trackFirstInteraction = useTrackFirstInteraction(variant)

    const steps = [
        <DashboardWrapper>
            <TitleBox>
                <Text variant="title-base">{content.title}</Text>

                <Text color="muted">{content.subtitle}</Text>
            </TitleBox>

            <ExpensesDashboard
                expenses={formsDataState.expenses}
                dispatch={(action) => {
                    dispatch(action)
                    if (
                        [
                            ActionType.EXPENSE_DATE_UPDATED,
                            ActionType.EXPENSE_DESCRIPTION_UPDATED,
                            ActionType.EXPENSE_MERCHANT_UPDATED,
                            ActionType.EXPENSE_CATEGORY_UPDATED,
                            ActionType.EXPENSE_PROJECT_UPDATED,
                            ActionType.EXPENSE_AMOUNT_UPDATED,
                        ].includes(action?.type)
                    ) {
                        trackFirstInteraction('first_interaction')
                    }
                }}
                onShowPreview={togglePreview}
                content={content}
                variant={variant}
            />
        </DashboardWrapper>,

        <AlmostThere
            formsDataState={formsDataState}
            dispatch={dispatch}
            content={content}
            setShareableLink={setShareableLink}
            variant={variant}
        />,

        <SuccessScreen
            {...content.successScreen[0]}
            shareableLink={shareableLink}
            variant={variant}
        />,
    ]

    const hasMounted = useHasMounted()
    const isSmallScreen = useMediaQuery.max(BREAKPOINT_SM)
    const {activeStepElement, activeStepIndex, totalSteps, navigate} = useStepWizard(steps, {
        onStepChange: scrollToTop,
    })

    if (!content) {
        return handleNoPrismicData()
    }

    if (!hasMounted) {
        return <LoadingDots />
    }

    if (isPreviewMode) {
        return (
            <Preview
                {...previewValues}
                content={content}
                onClosePreview={() => {}}
                variant={variant}
            />
        )
    }

    return (
        <>
            <PageStyles />

            {isSmallScreen && (
                <MobileHeader>
                    <RichText content={content.headerLink.richText} variant="spezia" />

                    {/* Only first step */}
                    {activeStepIndex === 0 && isPreviewVisible && (
                        <CloseBtn
                            onClick={togglePreview}
                            aria-label={globalStrings.close_button_text}
                        />
                    )}

                    {/* Only first step */}
                    {activeStepIndex === 0 && !isPreviewVisible && (
                        <NavTextButton onClick={togglePreview}>
                            {globalStrings.preview}
                        </NavTextButton>
                    )}

                    {/* All except first and last steps */}
                    {activeStepIndex > 0 && activeStepIndex < totalSteps - 1 && (
                        <NavTextButton onClick={navigate.prev}>{globalStrings.back}</NavTextButton>
                    )}
                </MobileHeader>
            )}

            {!isSmallScreen && !isPreviewVisible && (
                <Header ctaText={content.headerCta} ctaLink={content.headerCtaLink} />
            )}

            {isPreviewVisible ? (
                <Preview
                    {...formsDataState}
                    content={content}
                    onClosePreview={togglePreview}
                    variant={variant}
                />
            ) : (
                <Section padding="small">{activeStepElement}</Section>
            )}
        </>
    )
}

export const Head = ({
    location,
    pageContext,
    data,
}: GatsbyHeadProps<ExpenseReportGeneratorQuery>) => {
    const pageData = data.allPrismicExpenseReportGenerator.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={!pageData.allow_indexing}
        />
    )
}

const PageStyles = createGlobalStyle`
    html {
        background: ${colors.background.default};

        ${mq.min(BREAKPOINT_SM)} {
            background: ${colors.background.pink};
        }
    }
`

const MobileHeader = styled.header`
    position: sticky;
    top: 0;
    left: 0;
    z-index: ${zIndex.header};
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
    padding: ${tokens.spacing20};
    background: ${colors.background.default};
`

const NavTextButton = styled.button`
    ${text({variant: 'paragraph-base', color: 'muted'})}

    padding: unset;
    cursor: pointer;
    background: unset;
    border: unset;
`

const DashboardWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};

    ${mq.min(BREAKPOINT_SM)} {
        row-gap: ${tokens.spacing40};
    }
`

const TitleBox = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
    column-gap: ${tokens.spacing20};
    background: ${colors.background.default};
    border-radius: ${radii.arc8};

    ${mq.min(BREAKPOINT_SM)} {
        grid-template-columns: repeat(2, 1fr);
        padding: ${tokens.spacing40};
    }
`

const CloseBtn = styled.button`
    width: 14px;
    height: 14px;
    padding: unset;
    cursor: pointer;
    background: unset;
    background-image: url(${menuCloseIcon});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    border: unset;
`

export const query = graphql`
    query ExpenseReportGenerator($locale: String!) {
        allPrismicExpenseReportGenerator(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    allow_indexing
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    headerCta: header_cta
                    headerCtaLink: header_cta_link {
                        ...PrismicLink
                    }
                    headerLink: header_link {
                        richText
                    }
                    preview
                    title
                    subtitle
                    date
                    description
                    merchant
                    category
                    project
                    amount
                    addExpense: add_expense
                    generateExpenseReport: generate_expense_report
                    expensesDashboard: expenses_dashboard
                    deleteExpense: delete_expense
                    total
                    almostThere: almost_there {
                        title: almost_there_title
                        body {
                            richText
                        }
                        disclaimer {
                            richText
                        }
                        shareButtonText: share_button_text_fallback
                        downloadButtonText: download_button_text
                        shareTitle: share_title
                        shareText: share_text
                        shareUrl: share_url
                        mailToSubject: share_to_email_subject
                        mailToBody: share_to_email_body
                        yourCompanyName: your_company_name
                        yourName: your_name
                        yourFirstAndLastName: your_first_and_last_name
                        reportNumber: report_number
                        yourReportNumber: your_report_number
                        logoPlaceholder: logo_placeholder {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        generateReportButtonText: generate_report_button_text
                        receiveReportButtonText: receive_report_button_text
                    }
                    loadingImage: loading_fallback_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    loadingVideo: loading_video {
                        ...Video
                    }
                    loadingFacts: loading_facts {
                        fact
                    }
                    didYouKnow: did_you_know
                    reportGenerating: report_generating
                    expenseReport: expense_report
                    reportNumber: report_number
                    expense
                    employee
                    previewDisclaimer: preview_disclaimer
                    submittedBy: submitted_by
                    previewFooter: preview_footer {
                        richText
                    }
                    madeByPleo: made_by_pleo {
                        richText
                    }
                    successScreen: success_screen {
                        title: success_title
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        body: success_body
                        buttonLink: button_link {
                            ...PrismicLink
                        }
                        buttonText: button_text
                        createAnotherReport: create_another_report
                        explorePleoButtonText: explore_pleo_button_text
                        downloadReportButtonText: download_report_button_text
                        explorePleoButtonLink: explore_pleo_button_link {
                            ...PrismicLink
                        }
                    }
                    emailContent: email_content {
                        emailSubject: email_subject
                        helloThere: hello_there
                        emailBody: email_body
                        explorePleoButtonText: explore_pleo_button_text
                        explorePleoButtonLink: explore_pleo_button_link {
                            ...PrismicLink
                        }
                    }
                }
            }
        }
    }
`

export default ExpenseReportGenerator
